import { format, parseISO } from "date-fns";

import type { GlobalScheduleRule } from "~/api/global-schedule-rules.types";

const isYearlyRecurrence = (rrule: string) => /FREQ=YEARLY/i.test(rrule);

const formatDay = (isoDate: string, withYear: boolean) =>
  format(parseISO(isoDate), withYear ? "MMM d, yyyy" : "MMM d");

const formatDateRange = (rule: GlobalScheduleRule) => {
  const repeatsYearly = isYearlyRecurrence(rule.rrule);
  const withYear = !repeatsYearly;
  const start = formatDay(rule.startDate, withYear);
  const span =
    rule.endDate && rule.endDate !== rule.startDate
      ? `${start} - ${formatDay(rule.endDate, withYear)}`
      : start;

  return repeatsYearly ? `${span}, every year` : span;
};

const formatTimeWindow = (rule: GlobalScheduleRule) =>
  `${rule.startTime.slice(0, 5)} - ${rule.endTime.slice(0, 5)}`;

const getRuleFrequencyLabel = (rrule: string) => {
  const frequency = /FREQ=([A-Z]+)/i.exec(rrule)?.[1]?.toUpperCase();

  switch (frequency) {
    case "YEARLY":
      return "Yearly block";
    case "MONTHLY":
      return "Monthly block";
    case "WEEKLY":
      return "Weekly block";
    default:
      return "Block";
  }
};

const getRuleLabel = (rule: GlobalScheduleRule) =>
  rule.name ?? getRuleFrequencyLabel(rule.rrule);

interface GlobalRulesListProps {
  rules: GlobalScheduleRule[];
  isPending: boolean;
  isError?: boolean;
}

export const GlobalRulesList = ({
  rules,
  isPending,
  isError,
}: GlobalRulesListProps) => {
  if (isError) {
    return (
      <p className="py-3 text-sm text-red-700">
        Could not load the rule list. The counts above are still accurate.
      </p>
    );
  }

  if (isPending) {
    return (
      <div className="space-y-2">
        <div className="h-14 animate-pulse rounded-lg bg-neutral-100" />
        <div className="h-14 animate-pulse rounded-lg bg-neutral-100" />
        <div className="h-14 animate-pulse rounded-lg bg-neutral-100" />
      </div>
    );
  }

  if (rules.length === 0) {
    return (
      <p className="py-3 text-sm text-neutral-500">
        No individual rules to show.
      </p>
    );
  }

  return (
    <ul className="max-h-64 divide-y divide-neutral-100 overflow-y-auto rounded-xl border border-neutral-200">
      {rules.map((rule) => (
        <li key={rule.id} className="px-4 py-3">
          <p className="truncate text-sm font-semibold text-neutral-800">
            {getRuleLabel(rule)}
          </p>
          <p className="text-xs text-neutral-500">
            {formatDateRange(rule)} · {formatTimeWindow(rule)} · {rule.timezone}
          </p>
          {rule.exceptions.length > 0 && (
            <p className="text-xs text-violet-700">
              {rule.exceptions.length} provider exception
              {rule.exceptions.length !== 1 ? "s" : ""}
            </p>
          )}
        </li>
      ))}
    </ul>
  );
};
